import React, { useState } from 'react';
import {
  IAuditoriaDuplicidadePagamentosResultado,
  IItemDuplicidadePagamentos,
} from '../interfaces/IAuditoriaDuplicidadePagamentos';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaDuplicidadePagamentosResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaDuplicidadePagamentosDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const duplicidades = resultado.duplicidades || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Detecção e Encaminhamento de Erros de Perda Financeira — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Perda Financeira por Duplicidade</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Pagamentos em duplicidade (mesma nota e valor) sem estorno no mês.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / Grupos em Duplicidade</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-erro'>{duplicidades.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos manuais de Custo/Despesa (grupo 3) analisados no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada ao Quadro de Resumo de Perda Financeira.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div className='mt-2'><strong>Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3 className='mb-0'>Quadro de Resumo de Perda Financeira</h3>
        <p className='text-muted'>
          Pagamentos com a mesma nota fiscal e o mesmo valor, na mesma conta de Custo/Despesa e no mesmo mês,
          sem estorno correspondente. Clique em uma linha para ver os lançamentos (débito/crédito) que
          comprovam a duplicidade.
        </p>

        {duplicidades.length === 0 && (
          <div className='text-muted mt-4'>Nenhum pagamento em duplicidade identificado no período.</div>
        )}

        {duplicidades.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Mês</th>
                  <th>Nota</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-center'>Ocorrências</th>
                  <th className='text-end'>Perda Financeira</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {duplicidades.map((item: IItemDuplicidadePagamentos, idx) => {
                  const chave = `dp-${item.empresaId}-${item.contaContabil}-${item.numeroNota}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-erro' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>{item.mesCompetencia}</td>
                        <td>{item.numeroNota}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td className='text-center'>{item.quantidadeOcorrencias}</td>
                        <td className='text-end auditoria-col-highlight'>{formatCurrency(item.valorDuplicado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fff5f5' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>

                              <div className='mt-3'><strong>Lançamentos que comprovam a duplicidade:</strong></div>
                              <div className='table-responsive mt-2'>
                                <table className='auditoria-table'>
                                  <thead>
                                    <tr>
                                      <th>Data</th>
                                      <th>Nº Lançamento</th>
                                      <th>Histórico</th>
                                      <th className='text-end'>Débito</th>
                                      <th className='text-end'>Crédito</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.evidencias.map((ev, evIdx) => (
                                      <tr key={`${chave}-ev-${evIdx}`}>
                                        <td>{formatDate(ev.dataLancamento)}</td>
                                        <td>{ev.numeroLancamento}</td>
                                        <td style={{ maxWidth: 320 }}>{ev.historico}</td>
                                        <td className='text-end'>{formatCurrency(ev.valorDebito)}</td>
                                        <td className='text-end'>{formatCurrency(ev.valorCredito)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaDuplicidadePagamentosDetalhe;
